import { QueryKey, useQueryClient } from "@tanstack/react-query";
import {
  AuthTokenQueryData,
  authTokenQueryKey,
  useApi,
} from "./api-provider.js";
import { ApiError, FetchError } from "./errors.js";
import { getAuthHeaders, parseErrorResponse } from "./utils.js";
import { ExpiredTokenError, IncorrectPermissionsError } from "../lib/errors.js";

type WidgetsApiClient<T> = (data: {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  params?: Record<string, any>;
  headers?: Record<string, any>;
  data?: BodyType<unknown>;
  signal?: AbortSignal;
}) => Promise<T>;

export const useWidgetsApiClient = <T>(): WidgetsApiClient<T> => {
  const { authToken, baseUrl, elevatedAccess, widgetType } = useApi();
  const queryClient = useQueryClient();

  return async ({ url: pathname, method, params, data }) => {
    const url = new URL(baseUrl);
    url.pathname = pathname;

    if (params) {
      const cleanParams = Object.fromEntries(
        Object.entries(params).filter(([_, value]) => value !== undefined),
      );
      url.search = new URLSearchParams(cleanParams).toString();
    }

    const queryType = method.toLowerCase() === "get" ? "query" : "mutation";
    const buildHeaders = (token: string | undefined) => ({
      ...getAuthHeaders(token),
      ...(elevatedAccess && queryType === "mutation"
        ? { "x-elevated-access-token": elevatedAccess.token }
        : {}),
      "WorkOS-Widgets-Type": widgetType,
      "Content-Type": "application/json",
    });

    const sendRequest = (token: string | undefined) =>
      fetch(url, {
        method,
        cache: "no-store",
        headers: buildHeaders(token),
        ...(data ? { body: JSON.stringify(data) } : {}),
      }).catch((error) => {
        throw new FetchError({
          message: `Failed to ${method} ${url.toString()}`,
          queryType,
          context: { error },
        });
      });

    let response = await sendRequest(authToken);

    // On 403, try to re-mint the auth token and retry the request once. This
    // handles the window-focus race (data query fires before authToken refetch
    // resolves) and clock-skew cases where the proactive refresh timer in
    // ApiProvider didn't catch the expiration in time.
    if (response.status === 403) {
      const tokenQueryKey = authTokenQueryKey(widgetType);

      try {
        await queryClient.refetchQueries({ queryKey: tokenQueryKey });
      } catch (error) {
        // If the host app's authToken function threw (the most common true
        // "session expired"), surface this as ExpiredTokenError instead of
        // letting the 403 fall through to IncorrectPermissionsError.
        throw new ExpiredTokenError({
          context: { status: 403, queryType, refetchError: error },
        });
      }

      const refreshed =
        queryClient.getQueryData<AuthTokenQueryData>(tokenQueryKey);
      const freshToken = refreshed?.authToken;

      if (freshToken && freshToken !== authToken) {
        response = await sendRequest(freshToken);
      }
    }

    if (!response.ok) {
      const { message, status } = await parseErrorResponse(response);
      const ctx = {
        message,
        status,
        queryType,
      } as const;

      if (status === 403) {
        const refreshed = queryClient.getQueryData<AuthTokenQueryData>(
          authTokenQueryKey(widgetType),
        );
        const exp = refreshed?.exp;
        if (exp !== undefined && exp * 1000 <= Date.now()) {
          throw new ExpiredTokenError({ context: ctx });
        }
        throw new IncorrectPermissionsError({ context: ctx });
      }

      throw new ApiError(ctx);
    }

    return response.json();
  };
};

export type ErrorType<ErrorData> = ErrorData;

export type BodyType<BodyData> = BodyData & { headers?: any };

export const useWidgetsApiQueryOptions = <
  QueryOptions extends { queryKey: QK },
  QK extends QueryKey,
>(
  queryOptions: QueryOptions,
) => {
  const { permissions, baseUrl } = useApi();
  const queryKey = [
    // initial query key needs to go first to allow invalidation of the query name to work
    ...queryOptions.queryKey,
    permissions,
    baseUrl,
  ] as unknown as QK;

  return {
    ...queryOptions,
    queryKey: queryKey as QK,
  };
};
